import { useState, useEffect, useRef, useCallback } from 'react';
import { useDebounce } from '../../../../hooks/useDebounce';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Typography,
} from '@mui/material';
import useInternalBooking from '../../../../hooks/useInternalBooking';
import { useBooking } from '../../../../context/bookingContext';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { formatDurationLabel } from '../../../../util/formatHelper';

const minuteLabel = minutes => {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return null;
  }
  if (minutes % 60 === 0) {
    const hours = minutes / 60;
    return hours === 1 ? '1 Stunde' : `${hours} Stunden`;
  }
  return minutes === 1 ? '1 Minute' : `${minutes} Minuten`;
};

const parseCustomerDurationLabel = value => {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'number') {
    const normalized = value >= 1000 ? value : value * 60000;
    if (!Number.isFinite(normalized) || normalized <= 0) {
      return null;
    }
    const minutes = Math.round(normalized / 60000);
    return minuteLabel(minutes);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      return parseCustomerDurationLabel(numeric);
    }
    return trimmed;
  }

  return null;
};

function DurationSelect({ selectedDate, onDurationSelect }) {
  const [durations, setDurations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const activeRequestId = useRef(0);
  const lastFetchState = useRef({
    date: null,
    totalPersons: 0,
    overbooking: null,
    selectedResource: null,
  });
  const {
    selectedEntertainment,
    selectedProduct,
    selectedResource,
    selectedDuration,
    setSelectedDuration,
    selectedCategories,
    previousSelectedDate,
    setPreviousSelectedDate,
    overbooking,
    shouldWaitForResourceSelection,
    updateCapacity,
    maxAvailability,
    tempReservationId,
  } = useBooking();

  // See PersonSelect: the reservation the modal holds travels with the request
  // so it does not count against the capacity it is asking about. Read through a
  // ref so it never retriggers a fetch of its own.
  const tempReservationIdRef = useRef(tempReservationId);
  tempReservationIdRef.current = tempReservationId;

  const { getDurations } = useInternalBooking();

  const fetchDurations = useCallback(async () => {
    // Invalidate any in-flight request so its (now stale) response is ignored
    // when we decide not to fetch. Mirrors the guard used in TimeSelect.
    const invalidateInFlight = () => {
      activeRequestId.current += 1;
      setIsLoading(false);
    };

    if (
      !selectedDate ||
      !dayjs.isDayjs(selectedDate) ||
      !selectedDate.isValid() ||
      selectedCategories.length === 0
    ) {
      setDurations([]);
      if (!previousSelectedDate || !selectedDate.isSame(previousSelectedDate, 'day')) {
        setSelectedDuration(null);
      }
      invalidateInFlight();
      return;
    }

    // If product has resources and none is selected yet, don't make the call
    if (shouldWaitForResourceSelection(selectedProduct)) {
      invalidateInFlight();
      return;
    }

    const currentDate = selectedDate.format('YYYY-MM-DD');
    const currentTotalPersons = selectedCategories.reduce(
      (sum, cat) => sum + parseInt(cat.quantity || 0),
      0
    );

    const shouldFetch =
      !lastFetchState.current.date ||
      lastFetchState.current.date !== currentDate ||
      lastFetchState.current.totalPersons !== currentTotalPersons ||
      lastFetchState.current.overbooking !== overbooking ||
      lastFetchState.current.selectedResource !== selectedResource;

    if (!shouldFetch) {
      return;
    }

    // Tag this request so out-of-order responses can be discarded. Without this,
    // rapid person-count clicks fire overlapping duration requests, and a late
    // response for a smaller count could clobber the current durations, reset the
    // selected duration and hide the available start times (TimeSelect requires a
    // selected duration).
    const requestId = activeRequestId.current + 1;
    activeRequestId.current = requestId;
    setIsLoading(true);
    try {
      const formattedCategories = selectedCategories.map(category => ({
        name: category.name || 'Personen',
        count: parseInt(category.quantity) || 0,
      }));

      const data = await getDurations(
        selectedEntertainment,
        selectedProduct,
        selectedDate.year(),
        selectedDate.month(),
        selectedDate.date(),
        formattedCategories,
        overbooking,
        selectedResource,
        tempReservationIdRef.current
      );

      // Ignore responses that a newer request has already superseded.
      if (activeRequestId.current !== requestId) {
        return;
      }

      // Update lastFetchState immediately after successful API call
      lastFetchState.current = {
        date: currentDate,
        totalPersons: currentTotalPersons,
        overbooking,
        selectedResource,
      };

      if (data?.availableDurations) {
        setDurations(data.availableDurations);

        if (!previousSelectedDate || !selectedDate.isSame(previousSelectedDate, 'day')) {
          if (selectedDuration) {
            const isStillAvailable = data.availableDurations.some(
              d => d.durationMS === selectedDuration
            );
            if (!isStillAvailable) {
              setSelectedDuration(null);
            }
          }
          setPreviousSelectedDate(selectedDate);
        }
      } else {
        setDurations([]);
        if (!previousSelectedDate || !selectedDate.isSame(previousSelectedDate, 'day')) {
          setSelectedDuration(null);
          setPreviousSelectedDate(selectedDate);
        }
      }
    } catch (error) {
      if (activeRequestId.current !== requestId) {
        return;
      }
      console.error('Failed to fetch durations:', error);
      toast.error('Fehler beim Laden der verfügbaren Spieldauern.');
      setDurations([]);
      if (!previousSelectedDate || !selectedDate.isSame(previousSelectedDate, 'day')) {
        setSelectedDuration(null);
        setPreviousSelectedDate(selectedDate);
      }
    } finally {
      if (activeRequestId.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [
    selectedDate,
    selectedCategories,
    selectedEntertainment,
    selectedProduct,
    selectedResource,
    overbooking,
    getDurations,
    previousSelectedDate,
    selectedDuration,
    setPreviousSelectedDate,
    setSelectedDuration,
    shouldWaitForResourceSelection,
  ]);

  const debouncedFetchDurations = useDebounce(fetchDurations, 150);

  useEffect(() => {
    debouncedFetchDurations();
  }, [
    selectedDate,
    selectedResource,
    previousSelectedDate,
    overbooking,
    debouncedFetchDurations,
    shouldWaitForResourceSelection,
    selectedProduct,
  ]);

  useEffect(() => {
    const currentTotalPersons = selectedCategories.reduce(
      (sum, cat) => sum + parseInt(cat.quantity || 0),
      0
    );
    if (currentTotalPersons !== lastFetchState.current.totalPersons) {
      debouncedFetchDurations();
    }
  }, [selectedCategories, debouncedFetchDurations]);

  const handleDurationSelect = event => {
    const duration = event.target.value;
    setSelectedDuration(duration);
    onDurationSelect(duration);

    // Defer capacity update to next tick to avoid race with PersonSelect capacity update
    setTimeout(() => {
      const selectedDurObj = durations.find(d => d.durationMS === duration);
      updateCapacity(selectedDurObj);
    }, 0);
  };

  useEffect(() => {
    if (!selectedDuration) return;
    const selectedDurObj = durations.find(d => d.durationMS === selectedDuration);
    if (selectedDurObj) {
      updateCapacity(selectedDurObj);
    }
  }, [
    selectedDuration,
    selectedProduct,
    selectedResource,
    durations,
    maxAvailability,
    updateCapacity,
  ]);

  const totalPersons = selectedCategories.reduce(
    (sum, cat) => sum + parseInt(cat.quantity || 0),
    0
  );

  const isDurationEnabled = useCallback(
    duration => {
      const meetsMin =
        duration.minParticipants === null || totalPersons >= duration.minParticipants;
      const withinMax =
        duration.maxParticipants === null || totalPersons <= duration.maxParticipants;
      return meetsMin && withinMax;
    },
    [totalPersons]
  );

  useEffect(() => {
    if (!selectedDuration) return;
    const selectedDurObj = durations.find(d => d.durationMS === selectedDuration);
    if (!selectedDurObj) return;
    const withinMin =
      selectedDurObj.minParticipants === null || totalPersons >= selectedDurObj.minParticipants;
    const withinMax =
      selectedDurObj.maxParticipants === null || totalPersons <= selectedDurObj.maxParticipants;
    if (!(withinMin && withinMax)) {
      setSelectedDuration(null);
      onDurationSelect(null);
      updateCapacity(undefined);
    }
  }, [
    selectedDuration,
    durations,
    totalPersons,
    setSelectedDuration,
    onDurationSelect,
    updateCapacity,
  ]);

  useEffect(() => {
    if (durations.length !== 1) {
      return;
    }

    const singleDuration = durations[0];
    const enabled = isDurationEnabled(singleDuration);

    if (!enabled) {
      if (selectedDuration !== null) {
        setSelectedDuration(null);
        onDurationSelect(null);
        updateCapacity(undefined);
      }
      return;
    }

    if (selectedDuration !== singleDuration.durationMS) {
      setSelectedDuration(singleDuration.durationMS);
      onDurationSelect(singleDuration.durationMS);
    }

    updateCapacity(singleDuration);
  }, [
    durations,
    selectedDuration,
    setSelectedDuration,
    onDurationSelect,
    updateCapacity,
    isDurationEnabled,
  ]);

  return (
    <>
      {selectedCategories.length > 0 && (
        <>
          {durations.length === 1 && !isLoading ? (
            <Box sx={{ mt: { md: 2 }, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant='subtitle2' sx={{ color: 'text.secondary' }}>
                Dauer
              </Typography>
              <Typography variant='body1'>{`${formatDurationLabel(durations[0])}`}</Typography>
            </Box>
          ) : null}
          {durations.length > 1 && (
            <FormControl fullWidth sx={{ mt: { md: 2 } }}>
              <InputLabel id='duration-select-label'>Dauer</InputLabel>
              <Select
                labelId='duration-select-label'
                value={selectedDuration || ''}
                onChange={handleDurationSelect}
                label='Dauer'
                disabled={isLoading || selectedCategories.length === 0}
                endAdornment={
                  isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', pr: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : null
                }
              >
                {durations.map(duration => {
                  const isEnabled = isDurationEnabled(duration);
                  const customerLabel = parseCustomerDurationLabel(duration.customerDuration);
                  const displayText = customerLabel || formatDurationLabel(duration);

                  return (
                    <MenuItem
                      key={duration.durationMS}
                      value={duration.durationMS}
                      disabled={!isEnabled}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Typography>{displayText}</Typography>
                        {!isEnabled && (
                          <>
                            {duration.minParticipants !== null &&
                            duration.maxParticipants !== null ? (
                              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                {`Nur für ${duration.minParticipants} bis ${duration.maxParticipants} Teilnehmer`}
                              </Typography>
                            ) : duration.minParticipants !== null ? (
                              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                {`Nur ab ${duration.minParticipants} Teilnehmern`}
                              </Typography>
                            ) : duration.maxParticipants !== null ? (
                              <Typography variant='caption' sx={{ color: 'text.secondary' }}>
                                {`Nur bis ${duration.maxParticipants} Teilnehmer`}
                              </Typography>
                            ) : null}
                          </>
                        )}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
          {durations.length === 0 && isLoading ? (
            <Box sx={{ mt: { md: 2 }, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={20} />
              <Typography variant='body2'>Lade verfügbare Dauern...</Typography>
            </Box>
          ) : null}
          {durations.length === 0 && !isLoading ? (
            <Typography variant='body2' sx={{ mt: { md: 2 } }}>
              Keine verfügbaren Dauern gefunden
            </Typography>
          ) : null}
        </>
      )}
    </>
  );
}

export default DurationSelect;
